import React from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Route,
  Server,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Shuffle,
  BarChart3,
  Zap,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName as getProviderNameUtil } from "@/shared/utils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const routingModeIcons = {
  priority: Zap,
  round_robin: Shuffle,
  weighted: BarChart3,
  least_load: Server,
};

export default function MerchantRouting() {
  const { language, t } = useLanguage();
  
  // TODO: Implement custom API calls
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["routing-rules"],
    queryFn: async () => [], // TODO: Implement RoutingRule.list API call
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [], // TODO: Implement Provider.list API call
  });

  const getProviderName = (code) => {
    const provider = providers.find(p => p.code === code);
    if (!provider) return code;
    return getProviderNameUtil(provider, language);
  };

  return (
    <MerchantLayout currentPage="MerchantRouting">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("routingRulesTitle")}</h1>
        </div>
        <p className="text-slate-500">{t("configureProviderSelection")}</p>

        {/* Rules List */}
        <div className="space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-100">
                <Skeleton width="25%" height={24} className="mb-4" />
                <Skeleton width="50%" height={16} />
              </div>
            ))
          ) : rules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
              <Route className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">{t("noRoutingRules")}</p>
            </div>
          ) : (
            rules.map((rule, index) => {
              const ModeIcon = routingModeIcons[rule.routing_mode] || Zap;
              let providerSequence = [];
              try {
                providerSequence = JSON.parse(rule.provider_sequence || "[]");
              } catch (e) {}

              return (
                <motion.div
                  key={rule.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 border border-slate-100"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Route className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                        <p className="text-sm text-slate-500">Priority: {rule.priority}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={rule.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                        {rule.status === "active" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        {rule.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Transaction Type</p>
                      <p className="text-sm font-medium capitalize">{rule.transaction_type || "All"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Payment Method</p>
                      <p className="text-sm font-medium capitalize">{rule.payment_method?.replace(/_/g, " ") || "All"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Routing Mode</p>
                      <div className="flex items-center gap-1">
                        <ModeIcon className="h-4 w-4 text-emerald-600" />
                        <p className="text-sm font-medium capitalize">{rule.routing_mode?.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Failover</p>
                      <p className="text-sm font-medium">{rule.failover_enabled ? "Enabled" : "Disabled"}</p>
                    </div>
                  </div>

                  {providerSequence.length > 0 && (
                    <div className="pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Provider Sequence</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {providerSequence.map((code, i) => (
                          <React.Fragment key={code}>
                            <Badge variant="outline" className="bg-slate-50">
                              <Server className="h-3 w-3 mr-1" />
                              {getProviderName(code)}
                            </Badge>
                            {i < providerSequence.length - 1 && (
                              <ArrowRight className="h-4 w-4 text-slate-300" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </MerchantLayout>
  );
}